import {
  HotelBlockedAction,
  HotelCheckInResponse,
  HotelCheckOutResponse,
  HotelPackage,
  HotelPackageSlug,
  HotelPoint,
  HotelStatus,
} from "../types/hotel"

type RawRecord = Record<string, unknown>

const DEFAULT_RADIUS_M = 50
const HOTEL_BLOCKED_ACTIONS: HotelBlockedAction[] = [
  "battle",
  "scan",
  "treasure",
  "boss",
  "training",
  "qr",
]
const HOTEL_PACKAGE_SLUGS: HotelPackageSlug[] = ["coffee", "standard", "full"]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

function normalizePackageSlug(raw: unknown): HotelPackageSlug | null {
  const slug = pickString(raw).toLowerCase()
  return HOTEL_PACKAGE_SLUGS.includes(slug as HotelPackageSlug)
    ? (slug as HotelPackageSlug)
    : null
}

function normalizeBlockedAction(raw: unknown): HotelBlockedAction | null {
  const key = pickString(raw).toLowerCase()
  return HOTEL_BLOCKED_ACTIONS.includes(key as HotelBlockedAction)
    ? (key as HotelBlockedAction)
    : null
}

export function normalizeHotelPoint(raw: unknown): HotelPoint | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  const cityId = pickNumber(item.cityId ?? item.city_id)
  if (id == null || lat == null || lng == null || cityId == null) return null

  return {
    id: String(id),
    name: pickString(item.name ?? item.title, "Гостиница"),
    latitude: lat,
    longitude: lng,
    radiusMeters:
      pickNumber(
        item.radiusMeters ?? item.radius_meters ?? item.radiusM ?? item.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
    cityId,
  }
}

export function mapHotelPoints(data: unknown): HotelPoint[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapHotelPoints(JSON.parse(data))
    } catch {
      return []
    }
  }
  if (Array.isArray(data)) {
    return data
      .map(normalizeHotelPoint)
      .filter((point): point is HotelPoint => point != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.points ?? obj.items ?? obj.hotels ?? obj.hotelPoints ?? obj.hotel_points
  if (Array.isArray(list)) {
    return list
      .map(normalizeHotelPoint)
      .filter((point): point is HotelPoint => point != null)
  }
  const single = normalizeHotelPoint(obj)
  return single ? [single] : []
}

function normalizeHotelPackage(raw: unknown): HotelPackage | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const slug = normalizePackageSlug(item.slug)
  if (!slug) return null

  const dailyLimit =
    pickNumber(item.dailyLimit ?? item.daily_limit, 1) ?? 1
  const usedToday =
    pickNumber(item.usedToday ?? item.used_today, 0) ?? 0
  const remainingToday =
    pickNumber(
      item.remainingToday ?? item.remaining_today,
      Math.max(0, dailyLimit - usedToday)
    ) ?? Math.max(0, dailyLimit - usedToday)
  const priceCoins =
    pickNumber(item.priceCoins ?? item.price_coins, 0) ?? 0

  return {
    slug,
    title: pickString(item.title, slug),
    energy: pickNumber(item.energy, 0) ?? 0,
    restMinutes: pickNumber(item.restMinutes ?? item.rest_minutes, 0) ?? 0,
    priceCoins,
    basePriceCoins:
      pickNumber(item.basePriceCoins ?? item.base_price_coins, priceCoins) ??
      priceCoins,
    dailyLimit,
    usedToday,
    remainingToday,
    foreignPriceCoins:
      pickNumber(item.foreignPriceCoins ?? item.foreign_price_coins, priceCoins) ??
      priceCoins,
  }
}

export function normalizeHotelStatus(data: unknown): HotelStatus | null {
  const obj = unwrapPayload(data)
  const maxEnergy = pickNumber(obj.maxEnergy ?? obj.max_energy)
  const energy = pickNumber(obj.energy)
  if (maxEnergy == null || energy == null) return null

  const packagesRaw = obj.packages ?? obj.rooms ?? obj.hotelPackages ?? obj.hotel_packages
  const packages = Array.isArray(packagesRaw)
    ? packagesRaw
        .map(normalizeHotelPackage)
        .filter((pkg): pkg is HotelPackage => pkg != null)
    : []

  const blockedRaw = obj.blockedActions ?? obj.blocked_actions
  const blockedActions = Array.isArray(blockedRaw)
    ? blockedRaw
        .map(normalizeBlockedAction)
        .filter((action): action is HotelBlockedAction => action != null)
    : []

  const packageSlugRaw = obj.packageSlug ?? obj.package_slug
  const packageSlug = packageSlugRaw ? normalizePackageSlug(packageSlugRaw) : null

  const pointRaw = obj.hotelPoint ?? obj.hotel_point ?? obj.point

  return {
    energy,
    maxEnergy,
    isResting: pickBool(obj.isResting ?? obj.is_resting),
    restEndsAt: pickString(obj.restEndsAt ?? obj.rest_ends_at) || null,
    packageSlug,
    canCheckOut: pickBool(obj.canCheckOut ?? obj.can_check_out),
    blockedActions,
    hotelPoint: normalizeHotelPoint(pointRaw),
    radarWinsToday:
      pickNumber(obj.radarWinsToday ?? obj.radar_wins_today, 0) ?? 0,
    radarWinsForDiscount:
      pickNumber(obj.radarWinsForDiscount ?? obj.radar_wins_for_discount, 3) ?? 3,
    hasRadarDiscount: pickBool(
      obj.hasRadarDiscount ?? obj.has_radar_discount
    ),
    radarDiscountPercent:
      pickNumber(obj.radarDiscountPercent ?? obj.radar_discount_percent, 15) ??
      15,
    foreignPriceMultiplier:
      pickNumber(
        obj.foreignPriceMultiplier ?? obj.foreign_price_multiplier,
        1.5
      ) ?? 1.5,
    fundContributionRate:
      pickNumber(obj.fundContributionRate ?? obj.fund_contribution_rate, 0.5) ??
      0.5,
    packages,
  }
}

export function normalizeHotelCheckInResponse(
  data: unknown
): HotelCheckInResponse | null {
  const obj = unwrapPayload(data)
  const restEndsAt = pickString(obj.restEndsAt ?? obj.rest_ends_at)
  if (!restEndsAt) return null

  const blockedRaw = obj.blockedActions ?? obj.blocked_actions
  const blockedActions = Array.isArray(blockedRaw)
    ? blockedRaw
        .map(normalizeBlockedAction)
        .filter((action): action is HotelBlockedAction => action != null)
    : HOTEL_BLOCKED_ACTIONS

  return {
    restEndsAt,
    restMinutes: pickNumber(obj.restMinutes ?? obj.rest_minutes, 0) ?? 0,
    priceCoins: pickNumber(obj.priceCoins ?? obj.price_coins, 0) ?? 0,
    isResting: true,
    blockedActions,
    championFundCoins:
      pickNumber(obj.championFundCoins ?? obj.champion_fund_coins) ?? undefined,
    fundDelta: pickNumber(obj.fundDelta ?? obj.fund_delta) ?? undefined,
    fundContribution:
      pickNumber(obj.fundContribution ?? obj.fund_contribution) ?? undefined,
    coins: pickNumber(obj.coins) ?? undefined,
    energy: pickNumber(obj.energy) ?? undefined,
    maxEnergy: pickNumber(obj.maxEnergy ?? obj.max_energy) ?? undefined,
  }
}

export function normalizeHotelCheckOutResponse(
  data: unknown
): HotelCheckOutResponse | null {
  const obj = unwrapPayload(data)
  const energyBefore = pickNumber(obj.energyBefore ?? obj.energy_before)
  const energyAfter = pickNumber(obj.energyAfter ?? obj.energy_after)
  const energyGranted = pickNumber(obj.energyGranted ?? obj.energy_granted)
  if (energyBefore == null || energyAfter == null || energyGranted == null) {
    return null
  }

  return {
    energyBefore,
    energyAfter,
    energyGranted,
    isResting: false,
    energy: pickNumber(obj.energy) ?? energyAfter,
    maxEnergy: pickNumber(obj.maxEnergy ?? obj.max_energy) ?? undefined,
  }
}

export function normalizeHotelGeoBody(body: {
  pointId: string
  playerLat: number
  playerLng: number
}) {
  const pointId = String(body.pointId ?? "").trim()
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS для гостиницы")
  }
  return {
    pointId,
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  }
}

export function resolveHotelPackagePrice(
  pkg: HotelPackage,
  isForeignCity: boolean
): number {
  return isForeignCity ? pkg.foreignPriceCoins : pkg.priceCoins
}

export function formatHotelPackageEnergy(pkg: HotelPackage, maxEnergy: number): string {
  if (pkg.slug === "full") return `до ${maxEnergy}`
  return `+${pkg.energy}`
}

export function isHotelActionBlocked(
  blockedActions: HotelBlockedAction[],
  action: HotelBlockedAction
): boolean {
  return blockedActions.includes(action)
}

export const HOTEL_REST_BLOCK_LABELS: Record<HotelBlockedAction, string> = {
  battle: "Бои и дуэли",
  scan: "Скан точек",
  treasure: "Клад / звездопад",
  boss: "Босс",
  training: "Тренировка",
  qr: "QR-дуэль",
}
